/**
 * SectionDataHeatmap is responsible for rendering a full interactive heatmap for an entire section
 * of data e.g. THz, Inrared, metal, etc.
 */

import React, { useEffect, useRef  } from "react"
import colormap from 'colormap'
import { Position, SectionDataHeatmapProps } from "./SectionDataHeatmap.types"
import "./SectionDataHeatmap.css"

const SectionDataHeatmap = (props: SectionDataHeatmapProps) => {

  const {
    heatmaps,
    heatmapMask,
    width: CANVAS_WIDTH,
    scanPixelHeight: pixelHeight,
    selectedScan,
    scanPositionMetadata,
    overlayBoxes,
    highlightMin,
    highlightMax,
    interactionEnabled,
    colourPalette: COLOUR_PALETTE,
    canvasRef,
    skippedIndexes,
    onSelectScan
  } = props

  /**
   * Mouse position on the canvas.
   */
  const mousePositionRef = useRef<Position|null>(null)

  /**
   * Selected scan.
   */
  const selectedScanRef = useRef<number>(0)

  /**
   * Number of pixels gap between scans.
   */
  const SCAN_SPACING = 2

  /**
   * Size of each heatmap pixel.
   */
  const PIXEL_SIZE = { width: CANVAS_WIDTH/heatmaps[0][0].length, height: pixelHeight/heatmaps[0].length}

  /**
   * Number of pixels for the height of the canvas.
   */
  const CANVAS_HEIGHT = (heatmaps.length * PIXEL_SIZE.height * heatmaps[0].length) + (heatmaps.length* SCAN_SPACING)

  /**
   * Number of rows per scan.
   */
  const SCAN_ROW_HEIGHT = Math.floor(CANVAS_HEIGHT / heatmaps.length)

  /** 
   * Number of pixels height for each scan.
   */
  const SCAN_PIXEL_HEIGHT = heatmaps[0].length * PIXEL_SIZE.height

  /**
   * Threshold around 0 and 255 to trigger highlighting of min and max values.
   */
  const DATA_HIGHLIGHT_THRESHOLD = 5

  /**
   * heatmap colour map palette.
   */
  const colourMap = colormap({
    colormap: COLOUR_PALETTE,
    nshades: 256,
    format: 'hex',
    alpha: 1
  })

  useEffect(() => {
    selectedScanRef.current = selectedScan    
    draw()
  }, [canvasRef, PIXEL_SIZE, CANVAS_WIDTH, selectedScan, highlightMax, highlightMin, overlayBoxes, heatmaps, heatmapMask])

  /**
   * Draws all data onto the canvas i.e. heatmaps and highlighted scans.
   * 
   * @param context canvas context
   */
  const draw = () => {
    if (canvasRef?.current === null) { return }
    const context = canvasRef.current.getContext('2d', { willReadFrequently: true })
    if (context === null) { return }

    context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
    drawHeatmap(context)
    if (interactionEnabled === false) { return }
    drawScanMouseHoverHighlight(context)
    drawSelectedScanHighlight(context)
    drawScanInfoBox(context)
    drawBoundingBoxes(context)
  }

  /**
   * Draws full sections data onto the canvas as a heatmap.
   * 
   * @param context canvas context
   */
  const drawHeatmap = (context: CanvasRenderingContext2D) => {
    for (let scan=0; scan<heatmaps.length; scan++) {
      const scanColours = heatmaps[scan]

      for (let column=0; column < scanColours.length; column++) {
        const scanRow = scanColours[column]
        const rowLength = scanRow.length        
  
        for (let row=0; row<rowLength; row++) {
          context.fillStyle = sampleToHexColour(scanRow[row])
          
          if (heatmapMask && heatmapMask[scan] && heatmapMask[scan][row] === 0) {
            context.fillStyle = "transparent"
          }

          if (skippedIndexes !== undefined && skippedIndexes.includes(scan)) {
            context.fillStyle = "#2F2E35"
          }
          
          context.fillRect(
            Math.floor(row * PIXEL_SIZE.width), 
            (column * PIXEL_SIZE.height) + (scan * scanColours.length * PIXEL_SIZE.height) + (SCAN_SPACING*scan),
            Math.ceil(PIXEL_SIZE.width), 
            PIXEL_SIZE.height
          )
        }
      }
    }
  }

  /**
   * Draws highlighted box around scans based on mouse is hovering position.
   * 
   * @param context canvas context
   */
  const drawScanMouseHoverHighlight = (context: CanvasRenderingContext2D) => {
    if (mousePositionRef.current === null) { return }
    const scanNumber = Math.floor(mousePositionRef.current.y / SCAN_ROW_HEIGHT)
    drawScanHighlight(scanNumber, context)
  }

  /**
   * Draws highlighted around the selected scan.
   * 
   * @param context canvas context
   */
  const drawSelectedScanHighlight = (context: CanvasRenderingContext2D) => {    
    drawScanHighlight(selectedScanRef.current, context)
  }

  /**
   * Draws a highlighted border round a given scan.
   * 
   * @param scanNumber to draw box round
   * @param context canvas context
   */
  const drawScanHighlight = (scanNumber: number, context: CanvasRenderingContext2D) => {
    const y = scanNumber*SCAN_ROW_HEIGHT
    context.strokeStyle = "white"
    context.lineWidth = 2
    context.beginPath()
    context.roundRect(0, y, CANVAS_WIDTH, SCAN_PIXEL_HEIGHT, 5)
    context.stroke()
  }

  /**
   * Draws scan info box overlay for a scan.
   * 
   * @param context canvas context
   */
  const drawScanInfoBox = (context: CanvasRenderingContext2D) => {
    if (mousePositionRef.current === null) { return }
    const scanNumber = Math.floor(mousePositionRef.current.y / SCAN_ROW_HEIGHT)
    const y = scanNumber*SCAN_ROW_HEIGHT
    const boxHeight = 22
    const boxWidth = 140
    const offsetLeft = 10

    context.fillStyle = "#19161F"
    context.beginPath()
    context.roundRect(
      offsetLeft,
      y + (SCAN_PIXEL_HEIGHT - boxHeight)/2 ,
      boxWidth,
      boxHeight,
      5
    )
    context.fill()

    context.fillStyle = "white";
    context.font = `400 13px Hanken Grotesk`;
    context.textAlign = "left";
    context.textBaseline = "middle";

    context.fillText(
      scanPositionMetadata[scanNumber],
      offsetLeft + 10,
      (scanNumber*SCAN_ROW_HEIGHT) + (SCAN_ROW_HEIGHT / 2),
      boxWidth
    )
  }

  /**
   * Draws a set of overlay bounding boxes.
   * 
   * @param context canvas context
   * 
   */
  const drawBoundingBoxes = (context: CanvasRenderingContext2D) => {
    if (overlayBoxes === undefined) { return }

    for (let i=0; i<overlayBoxes.length; i++) {
      const vector = overlayBoxes[i]
      const boxStartScan = vector[1] / 4
      const boxEndScan = vector[3] / 4
      const totalScanCoverage = (boxStartScan + boxEndScan) - 1

      const spacingOffset = SCAN_SPACING * totalScanCoverage
      context.strokeStyle = "#6ADCF2"
      context.lineWidth = 4
      context.beginPath()
      context.roundRect(
        vector[0] * PIXEL_SIZE.width,
        vector[1] * PIXEL_SIZE.height + spacingOffset,
        vector[2] * PIXEL_SIZE.width,
        vector[3] * PIXEL_SIZE.height,
        5
      )
      context.stroke()
    }
  }

  /**
   * Selects a scan based on mouse click.
   * 
   * @param _ mouse down event
   */
  const onCanvasMouseDown = (_: React.MouseEvent<HTMLCanvasElement>) => {
    if (mousePositionRef.current === null) { return }
    const scanNumber = Math.floor(mousePositionRef.current.y / SCAN_ROW_HEIGHT)
    onSelectScan(scanNumber)
    draw()
  }

  /**
   * Redraws canvas without mouse scan highlight.
   */
  const onMouseLeaveCanvas = () => {
    mousePositionRef.current = null
    draw()
  }

  /**
   * Sets mouse x, y position on the canvas.
   * 
   * @param e mouse event
   */
  const setMousePos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    const x = (e.clientX - rect.left) * scaleX
    const y = (e.clientY - rect.top) * scaleY  

    if (mousePositionRef.current === null) {
      mousePositionRef.current = { x: x, y: y }
    } else {
      mousePositionRef.current.x = x
      mousePositionRef.current.y = y
    }
    draw()
  }

  /**
   * Converts a given 0-255 number to hex colour string.
   * 
   * @param sample 0-255 colour value to get hex value for
   * 
   * @returns hex colour string for a given 0-255 value
   */
  const sampleToHexColour = (sample: number): string => {
    // if (sample === 0) { return "transparent"}
    if (Math.ceil(sample) > 255 - DATA_HIGHLIGHT_THRESHOLD && highlightMax) { return "#ee0c0c"}
    if (Math.ceil(sample) < 0 + DATA_HIGHLIGHT_THRESHOLD && highlightMin) { return "#1282A2"}

    const i = Math.ceil(Math.max(0, Math.min(255, sample)))    
    return colourMap[i]
  }

  return (
    <div>
      <canvas
        className={`${(interactionEnabled === false) ? "" : "data-heatmap"}`}
        style={{borderRadius: "5px"}}
        ref={canvasRef} 
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        onMouseMove={setMousePos}
        onMouseDown={onCanvasMouseDown}
        onPointerLeave={onMouseLeaveCanvas}
      />
    </div>
  )
}

export default SectionDataHeatmap